import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search, Leaf, Drumstick } from "lucide-react";
import MenuItem from "@/components/MenuItem";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "starters", label: "Starters" },
  { key: "main_course", label: "Main Course" },
  { key: "rice_bread", label: "Rice & Bread" },
  { key: "desserts", label: "Desserts" },
  { key: "salads", label: "Salads" },
  { key: "indo_chinese", label: "Indo-Chinese" },
  { key: "raita", label: "Raita" },
];

export default function MenuPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(
    searchParams.get("category") || "all"
  );
  const [dietFilter, setDietFilter] = useState("all"); // all, veg, nonveg
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/menu`);
        setMenuItems(res.data);
      } catch (err) {
        console.error("Failed to load menu:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchMenu();
  }, []);

  useEffect(() => {
    const cat = searchParams.get("category");
    if (cat && CATEGORIES.find((c) => c.key === cat)) {
      setActiveCategory(cat);
    }
  }, [searchParams]);

  const handleCategoryChange = (key) => {
    setActiveCategory(key);
    if (key === "all") {
      setSearchParams({});
    } else {
      setSearchParams({ category: key });
    }
  };

  const filtered = menuItems.filter((item) => {
    if (activeCategory !== "all" && item.category !== activeCategory)
      return false;
    if (dietFilter === "veg" && !item.is_veg) return false;
    if (dietFilter === "nonveg" && item.is_veg) return false;
    if (
      searchQuery &&
      !item.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
      return false;
    return true;
  });

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-[#78350F] py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center animate-fade-up">
            <p className="font-body text-xs text-[#FED7AA] tracking-[0.2em] uppercase mb-2 font-semibold">
              Meals-O-Meals
            </p>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3">
              Our Menu
            </h1>
            <p className="font-body text-[#FED7AA]/80 text-sm sm:text-base max-w-lg mx-auto">
              Traditional Indian cuisine for your special events. Select items,
              choose your tray size, and place your order.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters Bar */}
        <div
          className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8"
          data-testid="menu-filters"
        >
          {/* Search */}
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input
              data-testid="menu-search"
              placeholder="Search dishes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white border-stone-200 focus:border-[#EA580C] focus:ring-[#EA580C]/20 rounded-xl h-10 font-body"
            />
          </div>

          {/* Diet Filter */}
          <div className="flex items-center gap-1.5 bg-white rounded-full p-1 border border-stone-200">
            <button
              data-testid="diet-all"
              onClick={() => setDietFilter("all")}
              className={`px-4 py-1.5 rounded-full text-xs font-body font-medium transition-all ${
                dietFilter === "all"
                  ? "bg-[#78350F] text-white"
                  : "text-stone-500 hover:text-stone-700"
              }`}
            >
              All
            </button>
            <button
              data-testid="diet-veg"
              onClick={() => setDietFilter("veg")}
              className={`px-4 py-1.5 rounded-full text-xs font-body font-medium transition-all flex items-center gap-1 ${
                dietFilter === "veg"
                  ? "bg-green-600 text-white"
                  : "text-stone-500 hover:text-stone-700"
              }`}
            >
              <Leaf className="h-3 w-3" /> Veg
            </button>
            <button
              data-testid="diet-nonveg"
              onClick={() => setDietFilter("nonveg")}
              className={`px-4 py-1.5 rounded-full text-xs font-body font-medium transition-all flex items-center gap-1 ${
                dietFilter === "nonveg"
                  ? "bg-red-600 text-white"
                  : "text-stone-500 hover:text-stone-700"
              }`}
            >
              <Drumstick className="h-3 w-3" /> Non-Veg
            </button>
          </div>
        </div>

        {/* Category Pills */}
        <div
          className="flex flex-wrap gap-2 mb-8"
          data-testid="category-pills"
        >
          {CATEGORIES.map((cat) => (
            <button
              key={cat.key}
              data-testid={`category-pill-${cat.key}`}
              onClick={() => handleCategoryChange(cat.key)}
              className={`px-5 py-2 rounded-full text-sm font-body font-medium transition-all ${
                activeCategory === cat.key
                  ? "bg-[#EA580C] text-white shadow-md"
                  : "bg-white text-stone-600 border border-stone-200 hover:border-[#EA580C]/30 hover:text-[#EA580C]"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Menu Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-xl border border-stone-100 p-5 animate-pulse"
              >
                <div className="h-4 bg-stone-100 rounded w-3/4 mb-3" />
                <div className="h-3 bg-stone-100 rounded w-full mb-2" />
                <div className="h-3 bg-stone-100 rounded w-2/3 mb-4" />
                <div className="flex gap-2 mb-4">
                  <div className="h-10 bg-stone-100 rounded-lg flex-1" />
                  <div className="h-10 bg-stone-100 rounded-lg flex-1" />
                  <div className="h-10 bg-stone-100 rounded-lg flex-1" />
                </div>
                <div className="h-9 bg-stone-100 rounded-full" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="text-center py-16"
            data-testid="no-results"
          >
            <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-4">
              <Search className="h-6 w-6 text-stone-400" />
            </div>
            <h3 className="font-heading text-xl text-[#78350F] mb-2">
              No dishes found
            </h3>
            <p className="font-body text-stone-500 text-sm">
              Try changing the filters or search term
            </p>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
            data-testid="menu-grid"
          >
            {filtered.map((item) => (
              <MenuItem key={item.id} item={item} />
            ))}
          </div>
        )}

        {/* Results Count */}
        {!loading && filtered.length > 0 && (
          <p
            className="text-center font-body text-xs text-stone-400 mt-8"
            data-testid="results-count"
          >
            Showing {filtered.length} dish
            {filtered.length !== 1 ? "es" : ""}
          </p>
        )}
      </div>
    </div>
  );
}
